// Centralized icon and color definitions for PDF badges using Lucide icons

import {
  BookOpen,
  Calendar,
  ClipboardList, // Added for Worksheet
  ClipboardCheck, // Added for Assessment
  Dices, // Changed from Dice5 to Dices
  FileCheck,
  FileText,
  ScrollText,
  PenTool,
  Plus,
  Minus,
  X,
  Divide,
  Compass,
  Layers,
  Star,
  HelpCircle,
  MessageSquare,
  Hash,
  Sigma,
  ListOrdered, // Import ListOrdered
  Calculator, // Import Calculator
  BookType,    // Added for Story
  FileEdit,    // Changed from FilePenLine
  Feather,     // Added for Easy difficulty
  MountainSnow, // Added for Hard difficulty (alternative)
  AlignJustify, // Added for Paragraph
  WholeWord,   // Added for Word (replaces Devanagari text)
  Combine      // Added for Combined
} from 'lucide-react';
// import CustomNumberSystemIcon from '../components/icons/CustomNumberSystemIcon'; // No longer needed if replaced

export const fallbackIcon = <HelpCircle className="w-4 h-4 text-gray-400" aria-hidden="true" />;

export const marathiLabels = {
  marathi: 'मराठी',
  letter: 'अक्षर',
  word: 'शब्द',
  sentence: 'वाक्य',
  paragraph: 'परिच्छेद',
  story: 'गोष्ट',
  Marathi: 'मराठी'
};

export const categoryIcons = {
  subject: {
    icon: <BookOpen className="w-4 h-4" aria-hidden="true" />,
    color: "blue",
    items: {
      marathi: { icon: <PenTool className="w-4 h-4" aria-hidden="true" />, label: "Marathi" }, // Changed to PenTool
      maths: { icon: <Calculator className="w-4 h-4" aria-hidden="true" />, label: "Maths" }, // Changed to Calculator
      default: { icon: fallbackIcon }
    }
  },
  subtopic: {
    icon: <PenTool className="w-4 h-4" aria-hidden="true" />, // General icon for subtopic category
    color: "green",
    items: {
      number_system: { icon: <ListOrdered className="w-4 h-4" /> }, // Replaced with ListOrdered
      addition: { icon: <Plus className="w-4 h-4" /> },
      subtraction: { icon: <Minus className="w-4 h-4" /> },
      multiplication: { icon: <X className="w-4 h-4" /> },
      division: { icon: <Divide className="w-4 h-4" /> },
      letter: { icon: <span className="font-bold">अ</span>, label: "अक्षर" }, // Keep Devanagari text for Letter
      word: { icon: <WholeWord className="w-4 h-4" />, label: "शब्द" },   // Use WholeWord for Word
      sentence: { icon: <MessageSquare className="w-4 h-4" /> },
      paragraph: { icon: <AlignJustify className="w-4 h-4" />, label: "परिच्छेद" }, // Changed to AlignJustify
      story: { icon: <BookType className="w-4 h-4" />, label: "Story" }, // Changed to BookType, added label
      combined: { icon: <Combine className="w-4 h-4" />, label: "Combined" }, // Added Combined subtopic
      default: { icon: fallbackIcon }
    }
  },
  type: {
    icon: <FileCheck className="w-4 h-4" aria-hidden="true" />,
    color: "purple",
    items: {
      worksheet: { icon: <FileEdit className="w-4 h-4" aria-hidden="true" />, label: "Worksheet" }, // Changed to FileEdit
      "lesson-plan": { icon: <ScrollText className="w-4 h-4" aria-hidden="true" />, label: "Lesson Plan" }, // Ensure key matches filter value
      game: { icon: <Dices className="w-4 h-4" aria-hidden="true" />, label: "Game" }, // Updated to Dices
      assessment: { icon: <ClipboardCheck className="w-4 h-4" aria-hidden="true" />, label: "Assessment" }, // Updated icon
      planner: { icon: <Calendar className="w-4 h-4" aria-hidden="true" />, label: "Planner" },
      default: { icon: fallbackIcon, label: "Type" }
    }
  },
  difficulty: {
    icon: <Compass className="w-4 h-4" aria-hidden="true" />,
    color: "yellow",
    items: {
      easy: { icon: <Feather className="w-4 h-4" aria-hidden="true" />, color: "yellow", label: "Easy" }, // Changed to Feather
      medium: { icon: <Layers className="w-4 h-4" aria-hidden="true" />, color: "yellow", label: "Medium" },
      challenging: { icon: <Star className="w-4 h-4" aria-hidden="true" />, color: "yellow", label: "Challenging" },
      hard: { icon: <MountainSnow className="w-4 h-4" aria-hidden="true" />, color: "yellow", label: "Hard" }, // Changed to MountainSnow
      default: { icon: fallbackIcon, color: "gray", label: "Difficulty" }
    }
  },
  digits: {
    icon: <Hash className="w-4 h-4" aria-hidden="true" />,
    color: "pink", // Changed from gray to pink to match App.js categoryIcons
    items: {
      english_numerals: {
        icon: (
          <span className="font-semibold inline-flex items-center justify-center h-4">
            123
          </span>
        ),
        label: "English Numerals",
      },
      devnagiri_numerals: {
        icon: (
          <span className="font-semibold inline-flex items-center justify-center h-4">
            १२३
          </span>
        ),
        label: "Devnagiri Numerals",
      },
      default: { icon: fallbackIcon, label: "Numerals" }
    }
  }
};

export const badgeBaseClass = "inline-flex items-center justify-start gap-1 pl-2 pr-3 py-1 rounded-full text-xs font-medium border shadow-sm transition-shadow";

// Color classes with Material-style soft background tint for badges
export const colorClasses = {
  red: 'bg-red-100 text-red-800 border-red-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  green: 'bg-green-100 text-green-800 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  amber: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  gray: 'bg-gray-100 text-gray-800 border-gray-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200'
};

// Define preferred order for filter categories and options within them
export const filterOrder = {
  categories: ['type', 'subject', 'subtopic', 'difficulty', 'digits'],
  options: {
    type: ['game', 'worksheet', 'lesson-plan', 'assessment', 'planner'], // Lowercase, hyphenated
    subject: ['maths', 'marathi'], // Lowercase
    subtopic: [ // Lowercase, underscored
      'number_system', 'addition', 'subtraction', 'multiplication', 'division', 'combined',
      'letter', 'word', 'sentence', 'paragraph', 'story'
    ],
    difficulty: ['easy', 'hard', 'medium', 'challenging'], // Updated order: Easy, Hard, Medium, Challenging
    digits: ['english_numerals', 'devnagiri_numerals'] // Lowercase, underscored
  }
};

const categoryStyles = {
  marathiLabels,
  categoryIcons,
  colorClasses
};       

export default categoryStyles;
